import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Fade,
  IconButton,
  Link,
  Typography,
} from "@mui/material";
import { keyframes } from "@mui/material/styles";
import { Close as CloseIcon, PlayCircle as PlayCircleIcon } from "@mui/icons-material";
import VideoPlayer from "src/@core/components/common/VideoPlayer";
import UserGuideTileExploreIcon from "src/@core/components/icons/UserGuideTileExploreIcon";
import UserGuideTileGroupsIcon from "src/@core/components/icons/UserGuideTileGroupsIcon";
import UserGuideTileHomeIcon from "src/@core/components/icons/UserGuideTileHomeIcon";
import UserGuideTilePostsIcon from "src/@core/components/icons/UserGuideTilePostsIcon";
import UserGuideTileSearchIcon from "src/@core/components/icons/UserGuideTileSearchIcon";
import UserGuideTileTicketIcon from "src/@core/components/icons/UserGuideTileTicketIcon";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  resolveUserGuideUserId,
  userGuideStorageKey,
} from "src/constants/userGuideStorage";
import {
  setShowCompleteProfileDialog,
  setShowUserGuide,
} from "src/redux/slices/AuthSlice";

const INTRO_HOLD_MS = [3000, 3500, 3000];
const INTRO_ENTER_MS = 1000;
const INTRO_GRID_STAGGER_MS = 72;
/** Delay after icon animation starts before labels use the same enter animation */
const INTRO_GRID_LABEL_AFTER_ICON_MS = Math.round(INTRO_ENTER_MS * 0.44);
const FEATURES_ENTER_MS = 700;

const ugGridCellEnter = keyframes`
  from { opacity: 0; transform: translate3d(0, 12px, 0); }
  to { opacity: 1; transform: translate3d(0, 0, 0); }
`;

const INTRO_TILE = 72;
const FEATURE_ROW_ICON = 40;
const DETAIL_ICON = 44;
const PURPLE_ACCENT = "#721AF0";

const INTRO_GRID_TILES = [
  {
    key: "home",
    Tile: UserGuideTileHomeIcon,
    labelKey: "userGuideTile_home",
    labelColor: "#721AF0",
    grad: "linear-gradient(145deg, #9B6FFF, #5C1AE0)",
    borderGrad: "linear-gradient(145deg, rgba(155,111,255,0.85), rgba(92,26,224,0.45))",
    shadow: "0 14px 38px rgba(114,26,240,0.28), 0 4px 12px rgba(114,26,240,0.12)",
  },
  {
    key: "people",
    Tile: UserGuideTileSearchIcon,
    labelKey: "userGuideTile_people",
    labelColor: "#FF7348",
    grad: "linear-gradient(145deg, #FF9B6A, #FF5B1F)",
    borderGrad: "linear-gradient(145deg, rgba(255,155,106,0.85), rgba(255,91,31,0.4))",
    shadow: "0 16px 44px rgba(255,122,0,0.2), 0 4px 14px rgba(255,122,0,0.12)",
  },
  {
    key: "groups",
    Tile: UserGuideTileGroupsIcon,
    labelKey: "userGuideTile_groups",
    labelColor: "#CD59FF",
    grad: "linear-gradient(145deg, #E080FF, #9B2FDF)",
    borderGrad: "linear-gradient(145deg, rgba(224,128,255,0.85), rgba(155,47,223,0.4))",
    shadow: "0 14px 38px rgba(205,89,255,0.26)",
  },
  {
    key: "places",
    Tile: UserGuideTileExploreIcon,
    labelKey: "userGuideTile_places",
    labelColor: "#FF6B4F",
    grad: "linear-gradient(145deg, #FF9472, #E8400A)",
    borderGrad: "linear-gradient(145deg, rgba(255,148,114,0.85), rgba(232,64,10,0.4))",
    shadow: "0 14px 36px rgba(255,124,40,0.24)",
  },
  {
    key: "events",
    Tile: UserGuideTileTicketIcon,
    labelKey: "userGuideTile_events",
    labelColor: "#5C6EF0",
    grad: "linear-gradient(145deg, #8EA4FF, #3A54DD)",
    borderGrad: "linear-gradient(145deg, rgba(142,164,255,0.85), rgba(58,84,221,0.4))",
    shadow: "0 14px 36px rgba(92,110,240,0.22)",
  },
  {
    key: "posts",
    Tile: UserGuideTilePostsIcon,
    labelKey: "userGuideTile_posts",
    labelColor: "#5B19C6",
    grad: "linear-gradient(145deg, #8860E0, #4A0FB8)",
    borderGrad: "linear-gradient(145deg, rgba(136,96,224,0.85), rgba(74,15,184,0.4))",
    shadow: "0 14px 38px rgba(91,25,198,0.24)",
  },
];

const USER_GUIDE_FEATURES = [
  { key: "home",        Tile: UserGuideTileHomeIcon,    grad: INTRO_GRID_TILES[0].grad, shadow: INTRO_GRID_TILES[0].shadow },
  { key: "nearby",      Tile: UserGuideTileSearchIcon,  grad: INTRO_GRID_TILES[1].grad, shadow: INTRO_GRID_TILES[1].shadow },
  { key: "communities", Tile: UserGuideTileGroupsIcon,  grad: INTRO_GRID_TILES[2].grad, shadow: INTRO_GRID_TILES[2].shadow },
  { key: "places",      Tile: UserGuideTileExploreIcon, grad: INTRO_GRID_TILES[3].grad, shadow: INTRO_GRID_TILES[3].shadow },
  { key: "events",      Tile: UserGuideTileTicketIcon,  grad: INTRO_GRID_TILES[4].grad, shadow: INTRO_GRID_TILES[4].shadow },
  { key: "posts",       Tile: UserGuideTilePostsIcon,   grad: INTRO_GRID_TILES[5].grad, shadow: INTRO_GRID_TILES[5].shadow },
];

/** Per-feature detail popup copy and layout; set `videoUrl` when assets are ready */
const USER_GUIDE_FEATURE_DETAILS = {
  home: {
    titleParts: [
      { key: "userGuideDetail_home_titleHighlight", highlight: true },
      { key: "userGuideDetail_home_titleRest", highlight: false },
    ],
    descKey: "userGuideDetail_home_desc",
    bullets: [],
    footer: { type: "single", linkKey: "userGuideDetail_home_footer" },
    videoBorder: false,
    videoUrl: null,
    videoPoster: null,
  },
  nearby: {
    titleParts: [
      { key: "userGuideDetail_nearby_titleHighlight", highlight: true },
      { key: "userGuideDetail_nearby_titleRest", highlight: false },
    ],
    descKey: "userGuideDetail_nearby_desc",
    bullets: [],
    footer: {
      type: "hintLink",
      hintKey: "userGuideDetail_nearby_footerHint",
      linkKey: "userGuideDetail_nearby_footerLink",
    },
    videoBorder: true,
    videoUrl: null,
    videoPoster: null,
  },
  communities: {
    titleParts: [
      { key: "userGuideDetail_communities_titleHighlight", highlight: true },
      { key: "userGuideDetail_communities_titleRest", highlight: false },
    ],
    descKey: "userGuideDetail_communities_desc",
    bullets: [],
    footer: {
      type: "hintLink",
      hintKey: "userGuideDetail_communities_footerHint",
      linkKey: "userGuideDetail_communities_footerLink",
    },
    videoBorder: true,
    videoUrl: null,
    videoPoster: null,
  },
  places: {
    titleParts: [
      { key: "userGuideDetail_places_titlePrefix", highlight: false },
      { key: "userGuideDetail_places_titleHighlight", highlight: true },
    ],
    descKey: "userGuideDetail_places_desc",
    bullets: [
      "userGuideDetail_places_bullet_1",
      "userGuideDetail_places_bullet_2",
      "userGuideDetail_places_bullet_3",
      "userGuideDetail_places_bullet_4",
    ],
    footer: {
      type: "hintLink",
      hintKey: "userGuideDetail_places_footerHint",
      linkKey: "userGuideDetail_places_footerLink",
    },
    videoBorder: true,
    videoUrl: null,
    videoPoster: null,
  },
  events: {
    titleParts: [
      { key: "userGuideDetail_events_titleHighlight", highlight: true },
      { key: "userGuideDetail_events_titleRest", highlight: false },
    ],
    descKey: "userGuideDetail_events_desc",
    bullets: [],
    footer: {
      type: "hintLink",
      hintKey: "userGuideDetail_events_footerHint",
      linkKey: "userGuideDetail_events_footerLink",
    },
    videoBorder: true,
    videoUrl: null,
    videoPoster: null,
  },
  posts: {
    titleParts: [
      { key: "userGuideDetail_posts_titleHighlight", highlight: true },
      { key: "userGuideDetail_posts_titleRest", highlight: false },
    ],
    descKey: "userGuideDetail_posts_desc",
    bullets: [],
    footer: {
      type: "hintLink",
      hintKey: "userGuideDetail_posts_footerHint",
      linkKey: "userGuideDetail_posts_footerLink",
    },
    videoBorder: true,
    videoUrl: null,
    videoPoster: null,
  },
};

// ─── Slide 1: icon tile cell (icon enters first, label same animation after) ─
function UserGuideIntroGridCell({
  Tile,
  labelKey,
  labelColor,
  grad,
  borderGrad,
  shadow,
  size,
  cellIndex,
  t,
}) {
  const iconDelay = cellIndex * INTRO_GRID_STAGGER_MS;
  const labelDelay = cellIndex * INTRO_GRID_STAGGER_MS + INTRO_GRID_LABEL_AFTER_ICON_MS;

  const iconBox = (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "18px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: grad,
        boxShadow: "inset 0 2px 8px rgba(255,255,255,0.22)",
        position: "relative",
        zIndex: 1,
        lineHeight: 0,
      }}
    >
      <Tile size={size * 0.62} />
    </Box>
  );

  const gradientBorderShell = (
    <Box
      sx={{
        borderRadius: "22px",
        padding: "2px",
        background: borderGrad,
        boxShadow: shadow,
        display: "inline-flex",
        animation: `${ugGridCellEnter} ${INTRO_ENTER_MS}ms cubic-bezier(0.4, 0, 0.2, 1) both`,
        animationDelay: `${iconDelay}ms`,
      }}
    >
      {iconBox}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.15, width: "100%" }}>
      {gradientBorderShell}
      <Typography
        component="span"
        sx={{
          display: "inline-block",
          fontSize: "0.875rem",
          fontWeight: 600,
          color: labelColor,
          textTransform: "lowercase",
          letterSpacing: "0.02em",
          animation: `${ugGridCellEnter} ${INTRO_ENTER_MS}ms cubic-bezier(0.4, 0, 0.2, 1) both`,
          animationDelay: `${labelDelay}ms`,
        }}
      >
        {t(labelKey)}
      </Typography>
    </Box>
  );
}

function UserGuideFeatureDetailDialog({ featureKey, onClose, t }) {
  const feature = USER_GUIDE_FEATURES.find((f) => f.key === featureKey);
  const detail = featureKey ? USER_GUIDE_FEATURE_DETAILS[featureKey] : null;
  const open = Boolean(featureKey && feature && detail);

  if (!feature || !detail) return null;

  const { Tile, grad, shadow } = feature;
  const { titleParts, descKey, bullets, footer, videoBorder, videoUrl, videoPoster } = detail;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 1500 }}
      PaperProps={{
        sx: {
          borderRadius: "24px",
          overflow: "hidden",
          maxHeight: "92vh",
          boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
        },
      }}
      slotProps={{ backdrop: { sx: { backdropFilter: "blur(2px)" } } }}
    >
      <DialogContent sx={{ p: { xs: 2.5, sm: 5 }, position: "relative" }}>
        <IconButton
          onClick={onClose}
          aria-label={t("close")}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "#999",
            zIndex: 1,
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box sx={{ display: "flex", gap: 2.5, alignItems: "center", pr: 4, mb: 1.25 }}>
          <Box
            sx={{
              width: DETAIL_ICON,
              height: DETAIL_ICON,
              minWidth: DETAIL_ICON,
              borderRadius: "50%",
              background: grad,
              boxShadow: shadow,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Tile size={DETAIL_ICON * 0.58} />
          </Box>
          <Typography
            component="div"
            sx={{ fontSize: "1.125rem", fontWeight: 700, lineHeight: 1.3 , height: "100%" }}
          >
            {titleParts.map(({ key, highlight }) => (
              <Box
                key={key}
                component="span"
                sx={{ color: highlight ? PURPLE_ACCENT : "#1a1a1a" }}
              >
                {t(key)}
              </Box>
            ))}
          </Typography>
        </Box>

        <Typography
          sx={{
            color: "#888",
            fontSize: "0.8125rem",
            lineHeight: 1.5,
            mb: bullets.length ? 1.25 : 2,
            pr: 1,
          }}
        >
          {t(descKey)}
        </Typography>

        {bullets.length > 0 && (
          <Box component="ul" sx={{ m: 0, mb: 2, pl: 2.25 }}>
            {bullets.map((bulletKey) => (
              <Typography
                key={bulletKey}
                component="li"
                sx={{
                  color: "#666",
                  fontSize: "0.8125rem",
                  lineHeight: 1.5,
                  mb: 0.35,
                  "&::marker": { color: PURPLE_ACCENT },
                }}
              >
                {t(bulletKey)}
              </Typography>
            ))}
          </Box>
        )}

        <Box
          sx={{
            borderRadius: "14px",
            overflow: "hidden",
            aspectRatio: "16 / 10",
            bgcolor: "#f0ebfa",
            ...(videoBorder && {
              border: `3px solid ${PURPLE_ACCENT}`,
            }),
          }}
        >
          {videoUrl ? (
            <VideoPlayer
              videoUrl={videoUrl}
              videoPoster={videoPoster}
              aspectRatio={16 / 10}
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(145deg, #EDE4FA 0%, #D8C4F5 100%)",
              }}
            >
              <PlayCircleIcon sx={{ fontSize: 48, color: "rgba(114,26,240,0.35)" }} aria-hidden />
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 2 }}>
          {footer.type === "single" ? (
            <Typography sx={{ color: "#1976d2", fontSize: "0.8125rem", lineHeight: 1.45 }}>
              {t(footer.linkKey)}
            </Typography>
          ) : (
            <>
              <Typography sx={{ color: "#999", fontSize: "0.75rem", lineHeight: 1.4 }}>
                {t(footer.hintKey)}
              </Typography>
              <Typography sx={{ color: "#1976d2", fontSize: "0.8125rem", lineHeight: 1.45, mt: 0.25 }}>
                {t(footer.linkKey)}
              </Typography>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main dialog ───────────────────────────────────────────────────────────
const UserGuideDialog = () => {
  const { t } = useTranslation("dashboard");
  const dispatch = useDispatch();
  const open = useSelector((state) => state.auth.showUserGuide);
  const user = useSelector((state) => state.auth.user);

  const [step, setStep] = useState(0);
  const [detailFeatureKey, setDetailFeatureKey] = useState(null);

  const displayName = React.useMemo(() => {
    const raw = (
      user?.firstName ||
      user?.fullName?.trim()?.split(/\s+/)?.[0] ||
      user?.userName ||
      t("userNameFallback")
    ).trim();
    if (!raw) return t("userNameFallback");
    return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
  }, [user, t]);

  useEffect(() => {
    if (!open) return undefined;
    setStep(0);
    setDetailFeatureKey(null);
    const d0 = INTRO_HOLD_MS[0];
    const d1 = INTRO_HOLD_MS[1];
    const d2 = INTRO_HOLD_MS[2];
    const t1 = setTimeout(() => setStep(1), d0);
    const t2 = setTimeout(() => setStep(2), d0 + d1);
    const t3 = setTimeout(() => setStep(3), d0 + d1 + d2);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [open]);

  const finishGuide = useCallback(() => {
    const uid = resolveUserGuideUserId(user);
    if (uid) {
      try { window.localStorage.setItem(userGuideStorageKey(uid), "1"); } catch { /* ignore */ }
    }
    dispatch(setShowUserGuide(false));
    if (user?.isProfileComplete === 0) {
      dispatch(setShowCompleteProfileDialog(true));
    }
  }, [dispatch, user]);

  const openFeatureDetail = useCallback((key) => {
    if (USER_GUIDE_FEATURE_DETAILS[key]) setDetailFeatureKey(key);
  }, []);

  const closeFeatureDetail = useCallback(() => {
    setDetailFeatureKey(null);
  }, []);

  return (
    <Dialog
      open={open && !!user}
      onClose={() => {}}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={step < 3}
      sx={{ zIndex: 1400 }}
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", maxHeight: "90vh" } }}
      slotProps={{ backdrop: { sx: { backdropFilter: "blur(2.5px)" } } }}
    >
      <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>

        {/* ── Slides 0–2 ── */}
        {step < 3 && (
          <Box sx={{ minHeight: 300, display: "flex", alignItems: "center", justifyContent: "center", px: 3, py: 4 }}>
            <Box
              key={step}
              sx={{
                width: "100%",
                maxWidth: step === 1 ? 400 : 360,
                mx: "auto",
                textAlign: "center",
                "@keyframes ugIntroEnter": {
                  from: { opacity: 0, transform: "translate3d(0,16px,0)" },
                  to:   { opacity: 1, transform: "translate3d(0,0,0)" },
                },
                ...(step !== 1 && {
                  animation: `ugIntroEnter ${INTRO_ENTER_MS}ms cubic-bezier(0.4,0,0.2,1) both`,
                }),
              }}
            >
              {step === 0 && (
                <Box
                  sx={{
                    width: "min(241px, 85vw)",
                    height: "min(241px, 85vw)",
                    mx: "auto",
                    borderRadius: "50%",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      "linear-gradient(180deg, #FFFFFF 0%, #FCFAFE 42%, #F2E8FA 100%)",
                  }}
                >
                  <Typography
                    component="div"
                    sx={{
                      fontSize: "1.75rem",
                      fontWeight: 700,
                      color: "#1a1a1a",
                      textAlign: "center",
                      px: 2,
                    }}
                  >
                    {t("userGuideHi")}{" "}
                    <Box component="span" sx={{ color: "#e6c200", fontWeight: 700 }}>
                      {displayName}
                    </Box>
                  </Typography>
                </Box>
              )}

              {/* ── Slide 1: icon grid ── */}
              {step === 1 && (
                <Box sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  columnGap: { xs: 2, sm: 2.5 },
                  rowGap: { xs: 2.5, sm: 3 },
                  maxWidth: 392,
                  mx: "auto",
                  justifyItems: "center",
                  alignItems: "start",
                }}>
                  {INTRO_GRID_TILES.map(({ key, Tile, labelKey, labelColor, grad, borderGrad, shadow }, cellIndex) => (
                    <UserGuideIntroGridCell
                      key={key}
                      Tile={Tile}
                      labelKey={labelKey}
                      labelColor={labelColor}
                      grad={grad}
                      borderGrad={borderGrad}
                      shadow={shadow}
                      cellIndex={cellIndex}
                      size={INTRO_TILE}
                      t={t}
                    />
                  ))}
                </Box>
              )}

              {/* ── Slide 2: let's show you around ── */}
              {step === 2 && (
                <Typography component="div" sx={{ fontSize: "1.5rem", fontWeight: 700, color: "#1a1a1a" }}>
                  {t("userGuideLetsShow")}{" "}
                  <Box component="span" sx={{ color: "#e6c200" }}>{t("userGuideAround")}</Box>
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* ── Slide 3: features list ── */}
        {step === 3 && (
          <Fade in timeout={FEATURES_ENTER_MS}>
            <Box sx={{ p: 5 }}>
              {/* header */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
                <Box sx={{ pr: 2 }}>
                  <Typography component="div" sx={{ fontSize: "1.625rem", fontWeight: 700, lineHeight: 1.2 }}>
                    <Box component="span" sx={{ color: "#1a1a1a" }}>{t("userGuideDiscoverPrefix")}{" "}</Box>
                    <Box component="span" sx={{ color: "#e6c200" }}>{t("userGuideDiscoverHighlight")}</Box>
                  </Typography>
                  <Typography sx={{ color: "#777", mt: 0.5, lineHeight: 1.45, fontSize: "0.8125rem" }}>
                    {t("userGuideDiscoverBlurb")}
                  </Typography>
                </Box>
                <Link
                  component="button" type="button" underline="hover" onClick={finishGuide}
                  sx={{ fontSize: "0.8rem", color: "#999", whiteSpace: "nowrap", cursor: "pointer", border: 0, background: "none" }}
                >
                  {t("skip")}
                </Link>
              </Box>

              {/* feature rows — timeline line centered on icon circles */}
              <Box sx={{ position: "relative" }}>
                {/* vertical line — left offset = icon radius (20px) + container left padding */}
                <Box sx={{
                  position: "absolute",
                  left: 19,         // center of 40px icon
                  top: 20,          // start from center of first icon
                  bottom: 60,       // stop before the button
                  width: 2,
                  bgcolor: "#e8e8e8",
                  borderRadius: 1,
                }} />

                {USER_GUIDE_FEATURES.map(({ key, Tile, grad, shadow }) => (
                  <Box key={key} sx={{ display: "flex", gap: 2, mb: 5.5, alignItems: "flex-start" }}>

                    {/* icon — plain gradient circle, NO extra wrapper box */}
                    <Box sx={{
                      width: FEATURE_ROW_ICON,
                      height: FEATURE_ROW_ICON,
                      minWidth: FEATURE_ROW_ICON,
                      borderRadius: "50%",
                      background: grad,
                      boxShadow: shadow,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      zIndex: 1,
                    }}>
                      <Tile size={FEATURE_ROW_ICON * 0.6} />
                    </Box>

                    {/* text */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", color: "#1a1a1a" }}>
                        {t(`userGuideFeature_${key}_title`)}
                      </Typography>
                      <Typography sx={{ color: "#666", mt: 0.25, lineHeight: 1.45, fontSize: "0.8125rem" }}>
                        {t(`userGuideFeature_${key}_desc`)}
                      </Typography>
                    </Box>

                    {/* watch — opens feature detail popup */}
                    <Link
                      component="button"
                      type="button"
                      onClick={() => openFeatureDetail(key)}
                      aria-label={`${t("userGuideWatchShort")}: ${t(`userGuideFeature_${key}_title`)}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        color: "#6c00e6",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        cursor: "pointer",
                        border: 0,
                        background: "none",
                        p: 0,
                        flexShrink: 0,
                        "&:hover": { color: "#5500b3" },
                      }}
                    >
                      {t("userGuideWatchShort")}
                      <PlayCircleIcon sx={{ fontSize: 16 }} aria-hidden />
                    </Link>
                  </Box>
                ))}
              </Box>

              <Button
                fullWidth variant="contained" onClick={finishGuide}
                sx={{
                  mt: 1, bgcolor: "#6c00e6", textTransform: "none",
                  fontWeight: 600, py: 1.25, borderRadius: 2, fontSize: "0.9375rem",
                  "&:hover": { bgcolor: "#5500b3" },
                }}
              >
                {t("userGuideStartExploring")}
              </Button>
            </Box>
          </Fade>
        )}
      </DialogContent>

      <UserGuideFeatureDetailDialog
        featureKey={detailFeatureKey}
        onClose={closeFeatureDetail}
        t={t}
      />
    </Dialog>
  );
};

export default UserGuideDialog;